import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { format, startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import {
    Box,
    Typography,
    Stack,
    Card,
    CardContent,
    Divider,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
    Alert,
    Collapse,
    useTheme,
} from "@mui/material";
import {
    ChevronLeft,
    ChevronRight,
    ExpandMore,
    ExpandLess,
    FlightTakeoff,
    AttachMoney,
    Receipt,
    WorkspacePremium,
} from "@mui/icons-material";
import api from "state/axios";
import useOnlineStatus from "./hooks/useOnlineStatus";
import useAuthCheck from "state/useAuthCheck";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { LAYOUT_HEIGHT } from "state/constants";


const SummaryCard = ({ icon, label, value, color }) => {    
    return (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: "16px !important" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ color: color || "primary.main" }}>
                        {icon}
                    </Box>
                    <Typography variant="subtitle1" color="text.secondary" noWrap>
                        {label}
                    </Typography>
                </Stack>
                <Typography variant="body1" fontWeight={600} noWrap>
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
};


const SessionRow = ({ session }) => {
    const [open, setOpen]       = useState(false);
    const [orders, setOrders]   = useState(null); // null = not loaded yet
    const [fetching, setFetching] = useState(false);

    const handleToggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && orders === null) {
            setFetching(true);
            try {
                const res = await api.get("/inflight/orders", { params: { sessionId: session._id } });
                setOrders(res.data || []);
            } catch {
                setOrders([]);
            } finally {
                setFetching(false);
            }
        }
    };

    const paymentLabel = { cash: "Tiền mặt", pos: "POS", qr: "QR" };

    return (
        <Box>
            {/* Session header row */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 1, cursor: "pointer" }}
                onClick={handleToggle}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {session.flightDesc} {session.dep}-{session.arr} {session.acType} {session.flightReg}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {session.flightDate ? format(new Date(session.flightDate), "dd/MM/yyyy") : ""}
                        {" "}{session.etd || session.std || ""} - {session.eta || session.sta || ""}
                    </Typography>
                </Box>

                <Chip
                    label={`${session.orderCount || 0} orders`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.65rem", height: 20, flexShrink: 0 }}
                />

                <Box sx={{ textAlign: "right", flexShrink: 0, minWidth: 60 }}>
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                        {(session.totalRevenue || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        +{(session.totalCommission || 0).toLocaleString()}
                    </Typography>
                </Box>

                <IconButton size="small" sx={{ p: 0 }}>
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
            </Stack>

            {/* Expanded order list */}
            <Collapse in={open}>
                <Box sx={{ pl: 1, pb: 1 }}>
                    {fetching ? (
                        <CircularProgress size={14} />
                    ) : !orders || orders.length === 0 ? (
                        <Typography variant="caption" color="text.disabled">
                            No orders found.
                        </Typography>
                    ) : orders.map((order, oi) => (
                        <Box
                            key={order._id || order.clientId}
                            sx={{
                                mb: 1,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                            }}
                        >
                            {/* Order header */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary">
                                        #{oi + 1}
                                    </Typography>
                                    {order.deviceTimestamp && (
                                        <Typography variant="caption" color="text.secondary">
                                            {format(new Date(order.deviceTimestamp), "HH:mm")}
                                        </Typography>
                                    )}
                                    <Chip
                                        label={paymentLabel[order.paymentMethod] || order.paymentMethod}
                                        size="small"
                                        sx={{ fontSize: "0.6rem", height: 16 }}
                                    />
                                    {(order.crewCode || order.crewName) && (
                                        <Typography variant="caption" color="text.secondary">
                                            {order.crewCode}{order.crewName ? ` - ${order.crewName}` : ""}
                                        </Typography>
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="caption" fontWeight={600} color="primary.main">
                                        {(order.totalAmount || 0).toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="success.main">
                                        +{(order.totalCommission || 0).toLocaleString()}
                                    </Typography>
                                </Stack>
                            </Stack>

                            {/* Order items */}
                            {order.items?.map((item, ii) => (
                                <Stack
                                    key={ii}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ pl: 1 }}
                                >
                                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                        {item.productName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                                        ×{item.quantity} = {(item.totalAmount || 0).toLocaleString()}
                                    </Typography>
                                </Stack>
                            ))}

                            {order.note ? (
                                <Typography variant="caption" color="text.disabled" sx={{ pl: 1, fontStyle: "italic" }}>
                                    {order.note}
                                </Typography>
                            ) : null}
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

const SalesStats = () => {
    useAuthCheck();
    const theme      = useTheme();
    const isOnline   = useOnlineStatus();
    const crewCode   = useSelector((state) => state.auth?.user?.crewCode);

    const [month, setMonth]               = useState(new Date());
    const [sessions, setSessions]         = useState([]);
    const [myCommission, setMyCommission] = useState(null);
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState(null);

    const load = useCallback(async (date) => {
        if (!isOnline || !crewCode) return;
        setLoading(true);
        setError(null);
        const params = {
            startDate: startOfMonth(date).toISOString(),
            endDate:   endOfMonth(date).toISOString(),
            crewCode,
        };
        try {
            const [sessRes, commRes] = await Promise.all([
                api.get("/inflight/sessions",            { params: { ...params, status: "closed" } }),
                api.get("/inflight/sessions/my-commission", { params }),
            ]);
            setSessions(sessRes.data || []);
            setMyCommission(commRes.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [isOnline, crewCode]);

    useEffect(() => {
        load(month);
    }, [month, load]);

    const totalRevenue    = sessions.reduce((s, sess) => s + (sess.totalRevenue    || 0), 0);
    const totalCommission = sessions.reduce((s, sess) => s + (sess.totalCommission || 0), 0);
    const totalOrders     = sessions.reduce((s, sess) => s + (sess.orderCount      || 0), 0);

    if (!isOnline) {
        return (
            <Box className="main-container-box">
                <FlexBetween>
                    <Header title="Sales Stats" subtitle="Inflight Statistics" />
                </FlexBetween>
                <Alert severity="warning" sx={{ alignItems: "center", mt: 2 }}>
                    Statistics require an internet connection.
                </Alert>
            </Box>
        );
    }

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Sales Stats" 
                    subtitle="Inflight Statistics" 
                />
            </FlexBetween>
            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: "auto" }}>
                {/* Month navigation */}
                <Stack 
                    direction="row" 
                    alignItems="center" 
                    justifyContent="center" 
                    spacing={1} 
                    mb={2}
                >
                    <Tooltip title="Previous month">
                        <IconButton 
                            size="small" 
                            onClick={() => setMonth((m) => subMonths(m, 1))}
                        >
                            <ChevronLeft />
                        </IconButton>
                    </Tooltip>
                    <Typography 
                        variant="subtitle1" 
                        fontWeight={600} 
                        sx={{ minWidth: 120, textAlign: "center" }}
                    >
                        {format(month, "MMMM yyyy")}
                    </Typography>
                    <Tooltip title="Next month">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setMonth((m) => addMonths(m, 1))}
                                disabled={format(month, "yyyy-MM") >= format(new Date(), "yyyy-MM")}
                            >
                                <ChevronRight />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {!loading && !error && (
                    <>
                        {/* Summary cards */}
                        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                            <SummaryCard
                                icon={<AttachMoney fontSize="small" />}
                                label="Revenue (VND)"
                                value={totalRevenue.toLocaleString()}
                                color={theme.palette.primary.main}
                            />
                            <SummaryCard
                                icon={<WorkspacePremium fontSize="small" />}
                                label="Commission (VND)"
                                value={totalCommission.toLocaleString()}
                                color={theme.palette.success.main}
                            />
                        </Stack>
                        <Stack 
                            direction="row" 
                            spacing={1} 
                            flexWrap="wrap" 
                            useFlexGap
                            mb={2} 
                        >
                            <SummaryCard
                                icon={<FlightTakeoff fontSize="small" />}
                                label="Flights"
                                value={sessions.length}
                                color={theme.palette.info.main}
                            />
                            <SummaryCard
                                icon={<Receipt fontSize="small" />}
                                label="Orders"
                                value={totalOrders}
                                color={theme.palette.warning.main}
                            />
                        </Stack>

                        {sessions.length === 0 ? (
                            <Alert severity="info" sx={{ alignItems: "center" }}>
                                No closed sessions found for {format(month, "MMMM yyyy")}.
                            </Alert>
                        ) : (
                            <>
                                {/* My shared commission */}
                                {myCommission && (
                                    <Card
                                        variant="outlined"
                                        sx={{ mb: 2, borderColor: "success.main", bgcolor: "action.hover" }}
                                    >
                                        <CardContent sx={{ p: "12px !important" }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                                                <WorkspacePremium fontSize="small" sx={{ color: "success.main" }} />
                                                <Typography variant="subtitle1" color="text.secondary">
                                                    My Commission
                                                </Typography>
                                            </Stack>
                                            <Typography variant="h6" fontWeight={600} color="success.main">
                                                {Math.round(myCommission.totalCommission).toLocaleString()} VND
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Sessions list */}
                                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                                    Sessions
                                </Typography>
                                <Card variant="outlined">
                                    <CardContent sx={{ p: "8px 12px !important" }}>
                                        {sessions.map((sess, idx) => (
                                            <Box key={sess._id}>
                                                {idx > 0 && <Divider />}
                                                <SessionRow session={sess} />
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default SalesStats;
